'use client';

import { useHasOverflow } from '@alltrails/core';
import { PropsWithChildren, ReactNode } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import useToggle from '@alltrails/shared/hooks/useToggle';
import classNames from 'classnames';
import logTrailDetailsShowMoreClicked from '@alltrails/amplitude/events/logTrailDetailsShowMoreClicked';
import type TrailDetails from '@alltrails/core/types/TrailDetails';
import LocalizationButton from '@alltrails/layout/components/LocalizationButton';
import IconButton from '@alltrails/denali/components/IconButton';
import EditIcon from '@alltrails/denali/icons/Edit';
import styles from './Description.module.scss';
import useTrailEditToolbar from '@/hooks/useTrailEditToolbar';

type Props = {
  hideLocalizationButton?: boolean;
  isBot: boolean;
  source?: ReactNode;
  trailDetails: TrailDetails;
  isAdminOrPLPUser?: boolean;
};

export default function Description({ children, hideLocalizationButton, isBot, source, trailDetails, isAdminOrPLPUser }: PropsWithChildren<Props>) {
  const [expanded, toggle] = useToggle();
  const [showSource, toggleShowSource] = useToggle();
  const intl = useIntl();
  const { ref, hasOverflow } = useHasOverflow<HTMLDivElement>();
  const { openTrailEditToolbar } = useTrailEditToolbar();

  return (
    <div className={classNames({ [styles.adminDescription]: isAdminOrPLPUser })}>
      <div
        className={classNames(styles.description, {
          [styles.clamp]: !isBot && !expanded,
          [styles.expanded]: expanded
        })}
        ref={ref}
      >
        {showSource ? source : children}
      </div>
      {!isBot && hasOverflow && (
        <>
          {expanded && ' '}
          <button
            className={classNames(styles.button, { [styles.more]: !expanded })}
            onClick={() => {
              toggle();

              logTrailDetailsShowMoreClicked({ trail_id: trailDetails.id, show_more_location: 'overview' });

              if (expanded && ref?.current) {
                ref.current.scrollIntoView();
              }
            }}
            type="button"
          >
            {expanded ? <FormattedMessage defaultMessage="less" /> : <FormattedMessage defaultMessage="more" />}
          </button>
        </>
      )}
      {source && !hideLocalizationButton && (
        <LocalizationButton showSource={showSource} toggleShowSource={toggleShowSource} className={styles.source} />
      )}
      {isAdminOrPLPUser && (
        <IconButton
          icon={{ Component: EditIcon }}
          className={classNames(styles.editIcon, { [styles.expanded]: expanded })}
          testId="edit-icon"
          variant="primary"
          onClick={() => openTrailEditToolbar('description')}
          size="sm"
          title={intl.formatMessage({ defaultMessage: 'Edit' })}
        />
      )}
    </div>
  );
}
